import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

export const AIBE_HISTORY_FLAG = "__tejCodexAibe";
export const AIBE_HOME_PATH = "/aibe-preparation";
export const AIBE_PRACTICE_PATH = "/aibe-preparation/practice";
export const AIBE_SYLLABUS_PATH = "/aibe-preparation/syllabus";

const DEFAULT_SCROLL = 0;

const getWindowHistoryState = () =>
  typeof window === "undefined" ? null : window.history.state || {};

const getCurrentUserState = () => {
  const browserState = getWindowHistoryState();
  return browserState && typeof browserState.usr === "object" && browserState.usr
    ? browserState.usr
    : {};
};

export const getAibeLocationState = (location) =>
  location?.state?.[AIBE_HISTORY_FLAG] ? location.state : null;

export const makeAibeUserState = (aibeState, previousState = {}, scrollY = DEFAULT_SCROLL) => ({
  ...previousState,
  [AIBE_HISTORY_FLAG]: true,
  aibeState,
  scrollY,
  aibeDepth:
    Number.isFinite(previousState?.aibeDepth) && previousState.aibeDepth >= 0
      ? previousState.aibeDepth
      : 0,
});

const getBrowserAibeUserState = () => {
  const userState = getCurrentUserState();
  return userState[AIBE_HISTORY_FLAG] ? userState : null;
};

const replaceCurrentUserState = (nextUserState) => {
  const browserState = getWindowHistoryState();
  if (!browserState) return;

  window.history.replaceState(
    {
      ...browserState,
      usr: nextUserState,
    },
    "",
  );
};

const normalizeQuestionIndex = (value) => {
  const number = Number.parseInt(value, 10);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};

export const buildAibeSearch = (aibeState) => {
  if (!aibeState || aibeState.screen !== "practice") return "";

  const params = new URLSearchParams();
  if (aibeState.view) params.set("view", aibeState.view);
  if (aibeState.mode) params.set("mode", aibeState.mode);
  if (aibeState.subjectId) params.set("subject", aibeState.subjectId);
  if (aibeState.paperId) params.set("paper", aibeState.paperId);
  if (Number.isFinite(aibeState.questionIndex)) {
    params.set("q", String(aibeState.questionIndex + 1));
  }

  const query = params.toString();
  return query ? `?${query}` : "";
};

export const getAibePathForState = (aibeState) => {
  if (aibeState?.screen === "syllabus") return AIBE_SYLLABUS_PATH;
  if (aibeState?.screen === "practice") return AIBE_PRACTICE_PATH;
  return AIBE_HOME_PATH;
};

export const parseAibeStateFromLocation = (location) => {
  const pathname = location?.pathname || AIBE_HOME_PATH;

  if (pathname === AIBE_SYLLABUS_PATH) {
    return { screen: "syllabus" };
  }

  if (pathname === AIBE_PRACTICE_PATH) {
    const params = new URLSearchParams(location.search || "");
    const view = params.get("view") || "setup";
    const mode = params.get("mode") || "";
    const state = {
      screen: "practice",
      view,
    };

    if (mode) state.mode = mode;
    if (params.get("subject")) state.subjectId = params.get("subject");
    if (params.get("paper")) state.paperId = params.get("paper");
    if (params.get("q")) {
      state.questionIndex = normalizeQuestionIndex(params.get("q")) - 1;
      if (state.questionIndex < 0) state.questionIndex = 0;
    }

    return state;
  }

  return { screen: "home" };
};

const requestScrollTo = (scrollY) => {
  if (typeof window === "undefined") return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      window.scrollTo(0, Math.max(0, scrollY || 0));
    });
  });
};

export function useAibeNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const latestLocationRef = useRef(location);

  const currentAibeState = useMemo(() => {
    const locationState = getAibeLocationState(location);
    return locationState?.aibeState || parseAibeStateFromLocation(location);
  }, [location]);

  useEffect(() => {
    latestLocationRef.current = location;
  }, [location]);

  const saveScrollForCurrentEntry = useCallback((stateOverride) => {
    if (typeof window === "undefined") return;

    const existingUserState = getCurrentUserState();
    const existingAibeState = existingUserState[AIBE_HISTORY_FLAG]
      ? existingUserState.aibeState
      : parseAibeStateFromLocation(latestLocationRef.current);

    replaceCurrentUserState({
      ...existingUserState,
      [AIBE_HISTORY_FLAG]: true,
      aibeState: stateOverride || existingAibeState,
      scrollY: window.scrollY || DEFAULT_SCROLL,
      aibeDepth:
        Number.isFinite(existingUserState.aibeDepth) && existingUserState.aibeDepth >= 0
          ? existingUserState.aibeDepth
          : 0,
    });
  }, []);

  const getCurrentDepth = useCallback(() => {
    const browserAibeState = getBrowserAibeUserState();
    return Number.isFinite(browserAibeState?.aibeDepth) ? browserAibeState.aibeDepth : 0;
  }, []);

  const pushAibeState = useCallback(
    (nextAibeState) => {
      saveScrollForCurrentEntry();

      const currentDepth = getCurrentDepth();
      const previousUserState = getCurrentUserState();
      const nextUserState = makeAibeUserState(
        nextAibeState,
        {
          ...previousUserState,
          aibeDepth: currentDepth + 1,
        },
        DEFAULT_SCROLL,
      );

      navigate(
        {
          pathname: getAibePathForState(nextAibeState),
          search: buildAibeSearch(nextAibeState),
        },
        { state: nextUserState },
      );
    },
    [getCurrentDepth, navigate, saveScrollForCurrentEntry],
  );

  const replaceAibeState = useCallback(
    (nextAibeState, options = {}) => {
      const existingUserState = getCurrentUserState();
      const nextUserState = makeAibeUserState(
        nextAibeState,
        existingUserState,
        options.scrollY ?? existingUserState.scrollY ?? DEFAULT_SCROLL,
      );

      navigate(
        {
          pathname: getAibePathForState(nextAibeState),
          search: buildAibeSearch(nextAibeState),
        },
        { replace: true, state: nextUserState },
      );
    },
    [navigate],
  );

  const backAibe = useCallback(
    (fallbackState = { screen: "home" }) => {
      saveScrollForCurrentEntry();

      if (getCurrentDepth() > 0) {
        navigate(-1);
        return;
      }

      replaceAibeState(fallbackState, { scrollY: DEFAULT_SCROLL });
    },
    [getCurrentDepth, navigate, replaceAibeState, saveScrollForCurrentEntry],
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previousRestoration;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let frameId = null;
    const updateScroll = () => {
      frameId = null;
      saveScrollForCurrentEntry();
    };

    const onScroll = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(updateScroll);
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", onScroll);
      saveScrollForCurrentEntry();
    };
  }, [saveScrollForCurrentEntry]);

  useEffect(() => {
    const existingUserState = getCurrentUserState();
    if (existingUserState[AIBE_HISTORY_FLAG]) return;

    replaceCurrentUserState(
      makeAibeUserState(parseAibeStateFromLocation(location), existingUserState, window.scrollY || 0),
    );
  }, [location]);

  useEffect(() => {
    const userState = getCurrentUserState();
    if (!userState[AIBE_HISTORY_FLAG]) return;

    if (navigationType === "POP") {
      requestScrollTo(userState.scrollY || DEFAULT_SCROLL);
      return;
    }

    requestScrollTo(DEFAULT_SCROLL);
  }, [location.key, location.pathname, location.search, navigationType]);

  return {
    currentAibeState,
    navigationType,
    pushAibeState,
    replaceAibeState,
    backAibe,
    saveScrollForCurrentEntry,
  };
}
